import React from "react";

import type { Code, InlineCode, PhrasingContent, Root } from "mdast";
import Markdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { twJoin } from "tailwind-merge";
import { visit } from "unist-util-visit";

import {
  ExecutedPromptSegment,
  ExecutedPromptVariables,
  interpolatePromptTemplate,
} from "src/aiNode/executedPromptsUtils";
import { Tooltip } from "src/design-system/Tooltip";

const VARIABLE_ELEMENT_TAG = "tktl-prompt-variable";

// Private-use characters that cannot appear in real prompt text, used to
// smuggle variable positions through the markdown parser so values are
// rendered as React chips instead of being interpreted as markdown.
const MARKER_START = "\uE000";
const MARKER_END = "\uE001";
const MARKER_PATTERN = /\uE000(\d+)\uE001/;
// Single capture group so String.split keeps whole markers as separators
// without also emitting the inner digits as extra parts.
const MARKER_SPLIT_PATTERN = /(\uE000\d+\uE001)/g;

const toMarkedMarkdown = (segments: ExecutedPromptSegment[]): string =>
  segments
    .map((segment, index) =>
      segment.isVariable
        ? `${MARKER_START}${index}${MARKER_END}`
        : // Escape raw HTML so prompt tags like <context> stay visible —
          // react-markdown drops HTML nodes instead of rendering them.
          segment.text.replace(/</g, "\\<"),
    )
    .join("");

const toVariableNode = (segment: ExecutedPromptSegment): PhrasingContent =>
  ({
    type: VARIABLE_ELEMENT_TAG,
    data: {
      hName: VARIABLE_ELEMENT_TAG,
      hProperties: {
        "data-text": segment.text,
        "data-expression": segment.isVariable ? segment.expression : "",
        "data-masked":
          segment.isVariable && segment.isMasked ? "true" : undefined,
      },
    },
  }) as unknown as PhrasingContent;

const remarkPromptVariables =
  (segments: ExecutedPromptSegment[]) => () => (tree: Root) => {
    visit(tree, "text", (node, index, parent) => {
      if (!parent || index === undefined) {
        return undefined;
      }
      const parts = node.value
        .split(MARKER_SPLIT_PATTERN)
        .filter((part) => part !== "" && part !== undefined);
      if (parts.length === 1 && !MARKER_PATTERN.test(parts[0])) {
        return undefined;
      }
      const replacement = parts.map((part): PhrasingContent => {
        const match = part.match(MARKER_PATTERN);
        const segment = match ? segments[Number(match[1])] : undefined;
        return segment
          ? toVariableNode(segment)
          : { type: "text", value: part };
      });
      parent.children.splice(index, 1, ...replacement);
      return index + replacement.length;
    });

    // Inside code blocks and spans element nodes cannot be injected, so
    // fall back to the raw substituted value there.
    visit(tree, ["code", "inlineCode"], (node) => {
      const codeNode = node as Code | InlineCode;
      codeNode.value = codeNode.value.replace(
        new RegExp(MARKER_PATTERN, "g"),
        (_, index) => {
          const segment = segments[Number(index)];
          return segment ? segment.text : "";
        },
      );
    });
  };

type PromptVariableChipProps = {
  "data-text"?: string;
  "data-expression"?: string;
  "data-masked"?: string;
};

const PromptVariableChip: React.FC<PromptVariableChipProps> = (props) => {
  const text = props["data-text"] ?? "";
  const expression = props["data-expression"] ?? "";
  const isMasked = props["data-masked"] === "true";

  return (
    <Tooltip
      placement="top"
      title={
        <>
          Value injected from <span className="font-code-12">{expression}</span>
        </>
      }
      body={
        isMasked
          ? "The value was masked because it comes from a field marked as sensitive."
          : undefined
      }
      asChild
    >
      <span
        data-loc="executed-prompt-variable"
        className={twJoin(
          "rounded-md border px-1",
          isMasked
            ? "border-gray-200 bg-gray-100 text-gray-500 italic"
            : "border-orange-200 bg-orange-50",
        )}
      >
        {text}
      </span>
    </Tooltip>
  );
};

const components = {
  [VARIABLE_ELEMENT_TAG]: PromptVariableChip,
} as Components;

type ExecutedPromptMarkdownProps = {
  template: string;
  variables: ExecutedPromptVariables;
};

export const ExecutedPromptMarkdown: React.FC<ExecutedPromptMarkdownProps> = ({
  template,
  variables,
}) => {
  const segments = interpolatePromptTemplate(template, variables);

  return (
    <div
      className="tktl-markdown break-words font-inter-normal-12px text-gray-800"
      data-loc="executed-prompt-text"
    >
      <Markdown
        components={components}
        remarkPlugins={[remarkGfm, remarkPromptVariables(segments)]}
      >
        {toMarkedMarkdown(segments)}
      </Markdown>
    </div>
  );
};
